/**
 * Pure cart-mutation logic for NewSaleScreen's `addNewCartItem`.
 *
 * WHY THIS IS ITS OWN MODULE (ATL-963 / Sentry QARASAY-KASSA-2B2 / -2RH):
 * this logic used to live inline in a `setCartItems(cartItems => { ... })` updater whose
 * "item is already present in cart AND needs the weight/price editor" branch called
 * `navigation.navigate('EditCartItem', ...)` and then FELL OFF THE END OF THE FUNCTION.
 * React stored that `undefined` as the new cart state, so:
 *
 *   1. the cashier's already-scanned lines vanished from the UI with no error at all
 *      (totalCartQuantity read 0, CartView rendered empty) — they re-rang the sale, or
 *      rang a short one;
 *   2. the next weighted/custom product navigated to EditCartItem carrying that undefined
 *      cart, and `[...cartItems]` in EditCartItemScreen.save / .deleteItemFromCart threw
 *      fatally in Hermes (`arraySpread`), killing the app mid-transaction.
 *
 * The fix is structural, not a guard at the throwing line: the state transition is a TOTAL
 * function that always returns an array, and the "open the editor" decision is returned as
 * DATA (`edit`) rather than performed as a side effect. Every exit path below returns.
 *
 * Callers must not re-add `cartItems ? [...cartItems] : []`-style normalisation around it —
 * three such guards existed in NewSaleScreen and cartPricing and are precisely what kept
 * this defect invisible for months. `cartItems` is initialised to `[]` and every writer now
 * writes an array; a non-array reaching here is a new defect and must be allowed to surface.
 *
 * That invariant is enforced at the one place external data becomes cart state: saved carts
 * are normalised as they are read (utils/savedCart.js, `normalizeSavedCarts`), because
 * SavedCartsModal's Select button assigns `item.cartItems` directly. Normalise at THAT
 * boundary, never here — a guard here cannot tell a broken record from an empty cart.
 */

/**
 * Index of a cart line matching `productId`, or -1.
 * Kept as substring matching to preserve the existing modifier-id convention
 * (`<productId>` is a prefix of a modifier line's composite id).
 */
export const findCartItemIndex = (productId, cartItems) =>
  cartItems.findIndex(item => item.id.indexOf(productId) > -1)

/**
 * True when adding this line should open the weight/price editor before it is finalised:
 * a weighted ("vesovoy") product added with the default quantity of 1, or a custom product.
 */
const needsEditor = (cartItem, weight, isModifier) =>
  Boolean((cartItem.type && weight === 1 && !isModifier) || cartItem.id === 'customProduct')

/**
 * @param {Array} cartItems           current cart (must be an array)
 * @param {Object} options
 * @param {Object} options.productInfo          the product being added (already stock-checked
 *                                              and ingredient-expanded by the caller)
 * @param {number} options.weight               quantity being added
 * @param {boolean} options.isModifier          this line is a modifier of an existing line
 * @param {number|null} options.modifiedProductIndex  index of the line being modified
 * @param {boolean} options.mergeSimilarItems   store setting: merge repeats into one line
 * @param {Object} options.defaultItemDiscount  discount option applied to a new line
 * @returns {{cartItems: Array, edit: {index: number, cartItem: Object}|null}}
 */
export const computeCartAddition = (cartItems, {
  productInfo,
  weight = 1,
  isModifier = false,
  modifiedProductIndex = null,
  mergeSimilarItems,
  defaultItemDiscount,
}) => {
  const newCartItems = [...cartItems]

  if (isModifier) {
    const modifiedCartItem = newCartItems[modifiedProductIndex]
    if (!modifiedCartItem) {
      // Loud rather than silent: a modifier with no parent line would otherwise be spliced
      // in at an arbitrary position and price into the sale under the wrong product.
      throw new Error(
        `Cannot add modifier "${productInfo?.name}": no cart line at index ${modifiedProductIndex}`,
      )
    }
    newCartItems.splice(modifiedProductIndex + 1, 0, {
      ...productInfo,
      quantity: weight,
      itemDiscount: defaultItemDiscount,
    })
    // Replace (not mutate) the parent line, so the previous state array is never written to.
    newCartItems.splice(modifiedProductIndex, 1, { ...modifiedCartItem, isModified: true })
    return { cartItems: newCartItems, edit: null }
  }

  let itemIndex = findCartItemIndex(productInfo.id, newCartItems)
  if (newCartItems[itemIndex]?.isModified) {
    // A modified line is its own thing; a repeat scan must start a fresh line.
    itemIndex = -1
  }
  if (mergeSimilarItems === false) {
    itemIndex = -1
  }

  if (itemIndex == null || itemIndex === -1) {
    // Not in the cart yet: append a new line.
    const cartItem = { ...productInfo, quantity: weight, itemDiscount: defaultItemDiscount }
    newCartItems.push(cartItem)
    return {
      cartItems: newCartItems,
      // `index` addresses newCartItems — the array the caller is about to commit. The old
      // code handed EditCartItem the PREVIOUS array with `index = newCartItems.length - 1`,
      // i.e. an index one past its end (a latent off-by-one on top of the crash).
      edit: needsEditor(cartItem, weight, isModifier) ? { index: newCartItems.length - 1, cartItem } : null,
    }
  }

  const cartItem = newCartItems[itemIndex]
  if (needsEditor(cartItem, weight, isModifier)) {
    // THE 2B2 BRANCH. The editor decides the final quantity/price for this line, so the cart
    // is committed unchanged — but it IS committed, as an array.
    return { cartItems: newCartItems, edit: { index: itemIndex, cartItem } }
  }

  newCartItems.splice(itemIndex, 1, { ...cartItem, quantity: cartItem.quantity + weight })
  return { cartItems: newCartItems, edit: null }
}
